import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiService } from "../services/apiService";
import * as FaIcons from "react-icons/fa";
const ArrowLeftIcon = FaIcons.FaArrowLeft as React.ElementType;

const SendSessionDetail: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<null | {
    session: any;
    results: Array<{
      send_session_id: string;
      contact_id: string;
      email: string;
      contact_name?: string | null;
      success: boolean;
      error_message?: string | null;
      error_code?: string | null;
      sent_at: string;
      opened_at?: string | null;
      retry_count?: number;
      last_retry_at?: string | null;
    }>;
    counts: { sent: number; delivered: number; opened: number; failed: number };
    last_sent_at: string | null;
  }>(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        if (!sessionId) throw new Error("Missing sessionId");
        const res = await apiService.getSendSessionDetails(sessionId);
        if (!mounted) return;
        setData(res.data);
      } catch (e: any) {
        console.error("Failed to load send session details", e);
        if (mounted) setError(e?.message || "Failed to load details");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [sessionId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black p-6 flex items-center justify-center">
        <div className="text-white text-lg">Loading...</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-black p-6">
        <div className="text-red-400">{error || "No data"}</div>
      </div>
    );
  }

  const { session, results, counts, last_sent_at } = data;

  return (
    <main className="min-h-screen bg-black p-6 max-w-6xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Email Details</h1>
        <button
          className="px-4 py-2 bg-black text-white border border-white hover:bg-gray-900 flex items-center gap-2 transition-colors"
          onClick={() => navigate(-1)}
        >
          <ArrowLeftIcon />
          <span>Back</span>
        </button>
      </div>

      {/* Email Summary Section */}
      <section className="mb-6 border border-white p-6 bg-black">
        <div className="text-xl font-semibold text-white mb-4">
          {session.email_subject}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6 text-sm">
          <div>
            <div className="text-gray-400">Status</div>
            <div className="font-semibold text-white">{session.status}</div>
          </div>
          <div>
            <div className="text-gray-400">Created</div>
            <div className="font-semibold text-white">
              {new Date(session.created_at).toLocaleString()}
            </div>
          </div>
          <div>
            <div className="text-gray-400">Last Sent</div>
            <div className="font-semibold text-white">
              {last_sent_at ? new Date(last_sent_at).toLocaleString() : "-"}
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4 text-center text-sm">
          <div className="border border-gray-700 bg-gray-900 p-4">
            <div className="text-gray-400">Sent</div>
            <div className="text-2xl font-bold text-white mt-1">
              {counts.sent}
            </div>
          </div>
          <div className="border border-green-700 bg-green-900/20 p-4">
            <div className="text-green-400">Delivered</div>
            <div className="text-2xl font-bold text-green-300 mt-1">
              {counts.delivered}
            </div>
          </div>
          <div className="border border-purple-700 bg-purple-900/20 p-4">
            <div className="text-purple-400">Opened</div>
            <div className="text-2xl font-bold text-purple-300 mt-1">
              {counts.opened}
            </div>
          </div>
          <div className="border border-red-700 bg-red-900/20 p-4">
            <div className="text-red-400">Failed</div>
            <div className="text-2xl font-bold text-red-300 mt-1">
              {counts.failed}
            </div>
          </div>
        </div>
      </section>

      {/* Recipients Table Section */}
      <section className="border border-white p-6 bg-black">
        <h2 className="text-lg font-semibold text-white mb-4">Recipients</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-400 border-b border-gray-700">
                <th className="py-3 pr-4 font-semibold">Email</th>
                <th className="py-3 pr-4 font-semibold">Name</th>
                <th className="py-3 pr-4 font-semibold">Status</th>
                <th className="py-3 pr-4 font-semibold">Sent At</th>
                <th className="py-3 pr-4 font-semibold">Opened</th>
              </tr>
            </thead>
            <tbody>
              {results.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-6 text-center text-gray-500">
                    No recipients found
                  </td>
                </tr>
              ) : (
                results.map((r) => (
                  <tr
                    key={`${r.send_session_id}-${r.email}`}
                    className="border-b border-gray-800 hover:bg-gray-900 transition-colors"
                  >
                    <td className="py-3 pr-4 text-white">{r.email}</td>
                    <td className="py-3 pr-4 text-gray-300">{r.contact_name || "-"}</td>
                    <td className="py-3 pr-4">
                      {r.success ? (
                        <span className="text-green-400">Delivered</span>
                      ) : (
                        <span className="text-red-400">Failed</span>
                      )}
                    </td>
                    <td className="py-3 pr-4 text-gray-300">
                      {new Date(r.sent_at).toLocaleString()}
                    </td>
                    <td className="py-3 pr-4 text-gray-300">
                      {r.opened_at ? new Date(r.opened_at).toLocaleString() : "-"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Email Content Section */}
      {session.email_body && (
        <section className="mt-6 border border-white p-6 bg-black">
          <h2 className="text-lg font-semibold text-white mb-4">Email Content</h2>
          <div
            className="prose prose-invert max-w-none text-gray-300"
            dangerouslySetInnerHTML={{ __html: session.email_body }}
          />
        </section>
      )}
    </main>
  );
};

export default SendSessionDetail;
